
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { ProfileView } from '@/types/profile-views';
import { debugLogger } from '@/utils/debugLogger';
import { VisibilityRules } from '@/core/BusinessRulesEngine/rules/VisibilityRules';

export const useProfileViews = () => {
  const { user } = useAuth();
  const { profile: profileData } = useProfile();
  const [views, setViews] = useState<ProfileView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfileViews = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      // ✅ Usar profileData do contexto (já carregado) ao invés de buscar novamente
      if (!profileData) {
        debugLogger.warn('ProfileViews', 'User profile not loaded yet');
        setLoading(false);
        return;
      }

      // ✅ Usar RPC otimizado ao invés de query JOIN complexa
      const { data, error: fetchError } = await supabase.rpc('get_profile_views');

      if (fetchError) throw fetchError;

      // Apply Business Rules filtering (async)
      const filteredViews = [];
      for (const view of data || []) {
        // Apply Business Rules using VisibilityRules
        const visibilityResult = await VisibilityRules.shouldShowProfile(
          {
            id: user.id,
            role: profileData?.role,
            hidden: profileData?.hidden || false,
            plan_type: 'free',
            verification_level: 'basic'
          },
          {
            id: view.viewer_id,
            role: view.viewer_role,
            hidden: view.viewer_hidden || false,
            plan_type: 'free',
            verification_level: 'basic'
          }
        );

        if (!visibilityResult.allowed) {
          debugLogger.debug('ProfileViews', 'Profile view filtered by Business Rules:', {
            viewId: view.id,
            reason: visibilityResult.reason,
            viewerId: view.viewer_id
          });
          continue;
        }

        // Additional check for onboarding completion (specific to profile views)
        if (!view.viewer_onboarding_completed) {
          debugLogger.debug('ProfileViews', 'Profile view filtered - incomplete onboarding:', {
            viewId: view.id,
            viewerId: view.viewer_id
          });
          continue;
        }

        // Transform RPC result to ProfileView format
        filteredViews.push({
          id: view.id,
          viewer_id: view.viewer_id,
          viewed_id: view.viewed_id,
          created_at: view.created_at,
          viewer_profile: {
            id: view.viewer_id,
            username: view.viewer_username,
            main_photo_url: view.viewer_main_photo_url,
            bio: view.viewer_bio,
            age: view.viewer_birth_date 
              ? new Date().getFullYear() - new Date(view.viewer_birth_date).getFullYear()
              : undefined,
            location: [view.viewer_city, view.viewer_state_code]
              .filter(Boolean)
              .join(', ') || undefined,
            occupation: view.viewer_occupation
          }
        });
      }

      const beforeFilterCount = data?.length || 0;
      const afterFilterCount = filteredViews.length;
      
      setViews(filteredViews);
      debugLogger.debug('ProfileViews', 'Fetched via RPC and filtered', {
        total: filteredViews.length,
        filtered: beforeFilterCount - afterFilterCount
      });
    } catch (err) {
      debugLogger.error('ProfileViews', 'Error fetching profile views:', err);
      setError('Erro ao carregar visualizações');
    } finally {
      setLoading(false);
    }
  }, [user?.id, profileData]);

  const recordProfileView = useCallback(async (viewedUserId: string): Promise<boolean> => {
    if (!user?.id || user.id === viewedUserId) return false;

    try {
      const { data, error } = await supabase.rpc('record_profile_view', {
        p_viewer_id: user.id,
        p_viewed_id: viewedUserId
      });

      if (error) throw error;

        debugLogger.debug('ProfileViews', 'Profile view recorded', {
        viewer: user.id, 
        viewed: viewedUserId,
        success: data 
      });

      return data || false;
    } catch (err) {
      debugLogger.error('ProfileViews', 'Error recording profile view:', err);
      return false;
    }
  }, [user?.id]);

  useEffect(() => {
    fetchProfileViews();
  }, [fetchProfileViews]);

  // Configurar real-time para novas visualizações
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('profile-views-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profile_views',
          filter: `viewed_id=eq.${user.id}`
        },
        () => {
          debugLogger.debug('ProfileViews', 'New profile view received, refreshing data');
          fetchProfileViews();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchProfileViews]);

  return {
    views,
    loading,
    error,
    recordProfileView,
    refreshViews: fetchProfileViews
  };
};
